import { Badge, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import TaskList from "./TaskList";

const DAY = 86400000;

// Whole-day offset of a task's (UTC-anchored, date-only) due date from today, in UTC — matching how
// the backend and the badges reckon "today". Null = no due date.
function dayOffset(task) {
  if (!task.dueDate) return null;
  const d = new Date(task.dueDate);
  const dueKey = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const todayKey = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((dueKey - todayKey) / DAY);
}

function bucketOf(task) {
  const diff = dayOffset(task);
  if (diff === null) return "nodate";
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff <= 7) return "thisweek";
  return "later";
}

const ORDER = ["overdue", "today", "tomorrow", "thisweek", "later", "nodate"];

// Agenda rendering: the same task rows, grouped into date buckets (Overdue / Today / Tomorrow /
// This week / Later / No date). Only non-empty buckets are shown.
export default function TaskAgenda({ tasks, emptyMessage, onToggleComplete, onToggleImportant, onEdit, onDelete, hideProjectTag = false }) {
  const { t } = useTranslation("Veft");

  if (!tasks.length) return <Text c="dimmed" ta="center" py="xl">{emptyMessage}</Text>;

  const groups = {};
  for (const task of tasks) (groups[bucketOf(task)] ??= []).push(task);

  return (
    <Stack gap="lg">
      {ORDER.filter((b) => groups[b]?.length).map((b) => (
        <Stack key={b} gap="xs">
          <Group gap="xs">
            <Text fw={600} size="sm" c={b === "overdue" ? "red" : undefined}>{t(`tasks_bucket_${b}`)}</Text>
            <Badge size="xs" variant="light" color={b === "overdue" ? "red" : "gray"}>{groups[b].length}</Badge>
          </Group>
          <TaskList
            tasks={groups[b]}
            emptyMessage=""
            hideProjectTag={hideProjectTag}
            onToggleComplete={onToggleComplete}
            onToggleImportant={onToggleImportant}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </Stack>
      ))}
    </Stack>
  );
}
